// Minimal Amplitude wiring. Nothing loads unless GATSBY_AMPLITUDE_API_KEY is
// set at build time, and browsers sending Do Not Track are never tracked.
//
// Events:
//   page_viewed      every route change (path, post slug, archived flag)
//   post_read        reader reached 50% / 90% of a post body
//   cta_clicked      a post or skills-page call to action (cta id, position)
//   outbound_clicked any other link that leaves the site

const SDK_URL = 'https://cdn.amplitude.com/libs/analytics-browser-2.26.0-min.js.gz';
const API_KEY = process.env.GATSBY_AMPLITUDE_API_KEY || '';

const state = { loading: false, ready: false, queue: [] };
const SESSION_KEY = 'afl-attribution-v1';
let sessionContext;

// Keep campaign labels, not arbitrary query strings or search terms.
const campaignValue = (value) => (
  typeof value === 'string' && /^[a-zA-Z0-9_.:-]{1,100}$/.test(value) ? value : undefined
);
const referrerOrigin = (value) => {
  try {
    const url = new URL(value);
    return /^https?:$/.test(url.protocol) ? url.origin : undefined;
  } catch (error) {
    return undefined;
  }
};
// Keep link identity without query strings, fragments, credentials or email recipients.
export const destinationForAnalytics = (value) => {
  try {
    const url = new URL(value);
    if (url.protocol === 'mailto:' || url.protocol === 'tel:') return url.protocol;
    if (!/^https?:$/.test(url.protocol)) return undefined;
    return `${url.origin}${url.pathname}`;
  } catch (error) {
    return undefined;
  }
};

const attribution = () => {
  if (sessionContext) return sessionContext;
  let stored;
  try {
    stored = JSON.parse(window.sessionStorage.getItem(SESSION_KEY));
  } catch (error) { /* Storage can be unavailable in private browser contexts. */ }
  const params = new URLSearchParams(window.location.search);
  const context = {};
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'].forEach((key) => {
    context[`landing_${key}`] = campaignValue(stored ? stored[`landing_${key}`] : params.get(key));
  });
  context.initial_referrer = referrerOrigin(stored ? stored.initial_referrer : document.referrer);
  context.is_test = params.get('afl_test') === '1' || !!(stored && stored.is_test);
  sessionContext = context;
  try {
    window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(context));
  } catch (error) { /* In-memory attribution still works without storage. */ }
  return context;
};

const dntEnabled = () => {
  if (typeof navigator === 'undefined') return true;
  return navigator.doNotTrack === '1' || window.doNotTrack === '1';
};

export const isEnabled = () => typeof window !== 'undefined' && !!API_KEY && !dntEnabled()
  && !attribution().is_test;

const flush = () => {
  const amp = window.amplitude;
  if (!amp) return;
  state.queue.splice(0).forEach(([name, props]) => amp.track(name, props));
};

const load = () => {
  if (state.loading || state.ready) return;
  state.loading = true;

  const script = document.createElement('script');
  script.src = SDK_URL;
  script.async = true;
  script.onload = () => {
    if (!window.amplitude) return;
    window.amplitude.init(API_KEY, undefined, {
      defaultTracking: {
        pageViews: false,
        sessions: false,
        formInteractions: false,
        fileDownloads: false
      }
    });
    state.ready = true;
    flush();
  };
  document.head.appendChild(script);
};

export const track = (name, props = {}) => {
  if (!isEnabled()) return;
  state.queue.push([name, { ...props, ...attribution(), site: window.location.hostname }]);
  if (state.ready) flush();
  else load();
};

const postSlug = (pathname) => {
  const match = /^\/posts\/([^/]+)\/?$/.exec(pathname || '');
  return match ? match[1] : undefined;
};

let readObserver;

const watchReadDepth = (pathname) => {
  if (readObserver) readObserver.disconnect();
  const body = document.querySelector('.content__body');
  const slug = postSlug(pathname);
  if (!body || !slug || typeof IntersectionObserver === 'undefined') return;

  const markers = [50, 90].map((depth) => {
    const marker = document.createElement('span');
    marker.setAttribute('aria-hidden', 'true');
    marker.dataset.readDepth = String(depth);
    marker.style.cssText = 'position:absolute;left:0;width:1px;height:1px;';
    marker.style.top = `${depth}%`;
    return marker;
  });

  if (getComputedStyle(body).position === 'static') body.style.position = 'relative';
  markers.forEach((m) => body.appendChild(m));

  readObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      track('post_read', { post_slug: slug, depth: Number(entry.target.dataset.readDepth) });
      readObserver.unobserve(entry.target);
    });
  });
  markers.forEach((m) => readObserver.observe(m));
};

export const onRouteUpdate = (location) => {
  if (!isEnabled() || !location) return;
  const archived = !!document.querySelector('[data-archived-post]');
  track('page_viewed', {
    path: location.pathname,
    post_slug: postSlug(location.pathname),
    archived,
    referrer: referrerOrigin(document.referrer)
  });
  // Wait a frame so the new route's DOM is in place.
  window.requestAnimationFrame(() => watchReadDepth(location.pathname));
};

let clicksWired = false;

export const wireClickTracking = () => {
  if (clicksWired || !isEnabled()) return;
  clicksWired = true;

  document.addEventListener('click', (event) => {
    const link = event.target.closest && event.target.closest('a[href]');
    if (!link) return;

    const slug = postSlug(window.location.pathname);
    if (link.dataset.cta) {
      track('cta_clicked', {
        cta_id: link.dataset.cta,
        cta_position: link.dataset.ctaPosition,
        destination: destinationForAnalytics(link.href),
        post_slug: slug,
        path: window.location.pathname
      });
      return;
    }

    if (link.host && link.host !== window.location.host) {
      track('outbound_clicked', {
        destination: destinationForAnalytics(link.href),
        destination_host: link.host,
        post_slug: slug,
        path: window.location.pathname
      });
    }
  }, true);
};
